// StepSynthesisManager.js - Version révisée pour cohérence entre Chat et Notes

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Eye, Clock, User, Bot, CheckCircle, HelpCircle,
  Target, BarChart3, TrendingUp, AlertCircle, Lightbulb
} from 'lucide-react';

/**
 * Hook pour gérer l'affichage synthétique dans le Chat
 */
export const useStepSynthesis = (competenceData, currentStepId, getStepNotes) => {
  const [synthesisData, setSynthesisData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Générer la synthèse à partir des notes existantes
  const generateSynthesisFromNotes = useCallback(() => {
    if (!currentStepId || !getStepNotes) return null;

    const stepNotes = getStepNotes(currentStepId);
    
    // Analyser les 3 sections
    const decisions = stepNotes.decisions || '';
    const questions = stepNotes.questions || '';
    const recommendations = stepNotes.ai_recommendations || '';

    // Compter les éléments dans chaque section
    const decisionCount = decisions.split('\n').filter(line => 
      line.trim() && (line.includes('[Utilisateur]') || line.includes('✅'))
    ).length;
    
    const questionCount = questions.split('\n').filter(line => 
      line.trim() && (line.includes('[Utilisateur]') || line.includes('❓'))
    ).length;
    
    const recommendationCount = recommendations.split('\n').filter(line => 
      line.trim() && line.includes('[Aladinn]')
    ).length;

    // Extraire les derniers éléments de chaque section pour aperçu
    const getLastElement = (content, icon) => {
      if (!content) return null;
      const lines = content.split('\n').filter(line => line.trim());
      const lastElementIndex = lines.findLastIndex(line => line.includes(icon));
      if (lastElementIndex === -1) return null;
      
      // Prendre quelques lignes après le marqueur
      const relevantLines = lines.slice(lastElementIndex, lastElementIndex + 3);
      return relevantLines.join(' ').replace(/[🤖👤✅❓📋]/g, '').replace(/\[.*?\]/g, '').trim();
    };

    const lastDecision = getLastElement(decisions, '✅');
    const lastQuestion = getLastElement(questions, '❓'); 
    const lastRecommendation = getLastElement(recommendations, '🤖');

    return {
      stepId: currentStepId,
      counts: {
        decisions: decisionCount,
        questions: questionCount,
        recommendations: recommendationCount,
        total: decisionCount + questionCount + recommendationCount
      },
      preview: {
        lastDecision,
        lastQuestion,
        lastRecommendation
      },
      hasContent: decisionCount > 0 || questionCount > 0 || recommendationCount > 0,
      lastUpdate: stepNotes.updatedAt || new Date().toISOString()
    };
  }, [currentStepId, getStepNotes]);

  // Mettre à jour la synthèse quand les notes changent
  useEffect(() => {
    const newSynthesis = generateSynthesisFromNotes();
    setSynthesisData(newSynthesis);
  }, [generateSynthesisFromNotes, competenceData?.stepNotes]);

  return {
    synthesisData,
    loading,
    generateSynthesisFromNotes
  };
};

/**
 * Composant d'affichage de la synthèse dans le Chat
 */
export const ChatSynthesisDisplay = ({ 
  stepId,
  stepTitle,
  synthesisData,
  onNavigateToNotes,
  className = ""
}) => {
  if (!synthesisData || !synthesisData.hasContent) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 border border-gray-200 ${className}`}>
        <div className="flex items-center gap-3 mb-3">
          <Target className="h-5 w-5 text-gray-500" />
          <h4 className="font-medium text-gray-700">Avancement sur cette étape</h4>
        </div>
        <div className="text-center py-4">
          <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-3">
            Aucune information enregistrée pour cette étape
          </p>
          <p className="text-xs text-gray-400">
            Répondez aux questions guidées pour commencer à suivre votre avancement
          </p>
        </div>
      </div>
    );
  }

  const { counts, preview } = synthesisData;

  return (
    <div className={`bg-white rounded-lg border border-blue-200 shadow-sm ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100 bg-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <BarChart3 className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900">Synthèse d'avancement</h4>
              <p className="text-sm text-blue-700">{stepTitle}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-blue-800">{counts.total}</div>
            <div className="text-xs text-blue-600">éléments</div>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
            <div className="text-lg font-semibold text-green-800">{counts.decisions}</div>
            <div className="text-xs text-green-600">Décisions</div>
          </div>
          <div className="text-center p-3 bg-orange-50 rounded-lg">
            <HelpCircle className="h-5 w-5 text-orange-600 mx-auto mb-1" />
            <div className="text-lg font-semibold text-orange-800">{counts.questions}</div>
            <div className="text-xs text-orange-600">Questions</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <Bot className="h-5 w-5 text-blue-600 mx-auto mb-1" />
            <div className="text-lg font-semibold text-blue-800">{counts.recommendations}</div>
            <div className="text-xs text-blue-600">Conseils</div>
          </div>
        </div>

        {/* Aperçu du contenu récent */}
        <div className="space-y-3">
          {preview.lastDecision && (
            <div className="flex items-start gap-3 p-2 bg-green-50 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-green-800 mb-1">Dernière décision</div>
                <div className="text-sm text-green-700 truncate">
                  {preview.lastDecision.length > 80 
                    ? `${preview.lastDecision.substring(0, 80)}...` 
                    : preview.lastDecision
                  }
                </div>
              </div>
            </div>
          )}

          {preview.lastQuestion && (
            <div className="flex items-start gap-3 p-2 bg-orange-50 rounded-lg">
              <HelpCircle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-orange-800 mb-1">Dernière question</div>
                <div className="text-sm text-orange-700 truncate">
                  {preview.lastQuestion.length > 80 
                    ? `${preview.lastQuestion.substring(0, 80)}...` 
                    : preview.lastQuestion
                  }
                </div>
              </div>
            </div>
          )}

          {preview.lastRecommendation && (
            <div className="flex items-start gap-3 p-2 bg-blue-50 rounded-lg">
              <Bot className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-blue-800 mb-1">Dernière recommandation</div>
                <div className="text-sm text-blue-700 truncate">
                  {preview.lastRecommendation.length > 80 
                    ? `${preview.lastRecommendation.substring(0, 80)}...` 
                    : preview.lastRecommendation
                  }
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bouton d'accès aux notes détaillées */}
        {onNavigateToNotes && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <button
              onClick={onNavigateToNotes}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Voir toutes les notes détaillées
            </button>
          </div>
        )}

        {/* Horodatage */}
        <div className="mt-3 text-xs text-gray-500 text-center flex items-center justify-center gap-1">
          <Clock className="h-3 w-3" />
          Dernière mise à jour : {new Date(synthesisData.lastUpdate).toLocaleString('fr-FR')}
        </div>
      </div>
    </div>
  );
};

/**
 * Composant compact pour affichage dans la sidebar
 */
export const SynthesisSummary = ({ 
  synthesisData, 
  stepTitle,
  onClick,
  className = "" 
}) => {
  if (!synthesisData || !synthesisData.hasContent) {
    return (
      <div className={`p-3 bg-gray-50 rounded-lg border border-gray-200 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-600">Avancement</span>
        </div>
        <div className="text-xs text-gray-500">Aucune donnée</div>
      </div>
    );
  }

  const { counts } = synthesisData;
  const progress = Math.min((counts.total / 10) * 100, 100); // Assume 10 éléments = 100%

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left ${className}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-800">Avancement</span>
        </div>
        <span className="text-xs text-gray-500">{counts.total}</span>
      </div>
      
      <div className="flex justify-between text-xs mb-2">
        <span className="text-green-600">{counts.decisions} décisions</span>
        <span className="text-orange-600">{counts.questions} questions</span>
        <span className="text-blue-600">{counts.recommendations} conseils</span>
      </div>
      
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div 
          className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </button>
  );
};

/**
 * Indicateur de progression pour le header
 */
export const ProgressIndicator = ({ 
  synthesisData,
  className = ""
}) => {
  if (!synthesisData || !synthesisData.hasContent) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="w-2 h-2 rounded-full bg-gray-300" />
        <span className="text-xs text-gray-500">Pas de données</span>
      </div>
    );
  }

  const { counts } = synthesisData;
  const hasDecisions = counts.decisions > 0;
  const hasQuestions = counts.questions > 0;
  const hasRecommendations = counts.recommendations > 0;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className={`w-2 h-2 rounded-full ${hasDecisions ? 'bg-green-500' : 'bg-gray-300'}`} />
      <div className={`w-2 h-2 rounded-full ${hasQuestions ? 'bg-orange-500' : 'bg-gray-300'}`} />
      <div className={`w-2 h-2 rounded-full ${hasRecommendations ? 'bg-blue-500' : 'bg-gray-300'}`} />
      <span className="text-xs text-gray-600">{counts.total}</span>
    </div>
  );
};

/**
 * Notification de mise à jour automatique
 */
export const SynthesisUpdateNotification = ({
  show,
  synthesisData,
  onView,
  onDismiss,
  autoHide = true,
  duration = 5000
}) => {
  useEffect(() => {
    if (show && autoHide) {
      const timer = setTimeout(onDismiss, duration);
      return () => clearTimeout(timer);
    }
  }, [show, autoHide, duration, onDismiss]);

  if (!show || !synthesisData) return null;

  return (
    <div className="fixed bottom-6 right-6 bg-white border border-blue-200 rounded-lg shadow-lg p-4 max-w-sm z-50">
      <div className="flex items-start gap-3">
        <TrendingUp className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-gray-800">Avancement mis à jour</div>
          <div className="text-sm text-gray-600 mt-1">
            {synthesisData.counts.total} éléments enregistrés pour cette étape
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={onView}
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            >
              Voir
            </button>
            <button
              onClick={onDismiss}
              className="text-xs text-gray-600 hover:text-gray-800"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default {
  useStepSynthesis,
  ChatSynthesisDisplay,
  SynthesisSummary,
  ProgressIndicator,
  SynthesisUpdateNotification
};